import { MAIL_SPLITS, type MailSplitId, normalizeEmail } from '@daimon-mail/mail';

// -----------------------------------------------------------------------------
// Constants
// -----------------------------------------------------------------------------

const QUERY_WORD_PATTERN =
  /(?:(?:from|sender|to|involves|before|after|has|is|label|in):[ \t]*(?![A-Za-z_]+:))?(?:[^\s"]+|"[^"]*"?)+/giu;
const FILTER_WORD_PATTERN = /^([A-Za-z_]+):(.*)$/su;
const DATE_PATTERNS = [
  /^(?<year>\d{4})[-/](?<month>\d{1,2})[-/](?<day>\d{1,2})$/u,
  /^(?<month>\d{1,2})\/(?<day>\d{1,2})\/(?<year>\d{4})$/u,
];
const THREAD_SEARCH_STATES = ['read', 'starred', 'unread'] as const;

// -----------------------------------------------------------------------------
// Types
// -----------------------------------------------------------------------------

export type ThreadSearchState = (typeof THREAD_SEARCH_STATES)[number];

/**
 * Structured filters parsed from a search query. Values within one key are OR'd together;
 * distinct keys are AND'd. Dates are epoch milliseconds at local start of day: `after` is an
 * inclusive lower bound on the thread's newest message, `before` an exclusive upper bound on
 * its oldest message.
 */
export type ThreadSearchFilters = {
  after?: number;
  before?: number;
  from: string[];
  involves: string[];
  hasAttachment?: boolean;
  is: ThreadSearchState[];
  labels: string[];
  splitIds: MailSplitId[];
  to: string[];
};

export type ThreadSearchQuery = {
  filters: ThreadSearchFilters;
  hasFilters: boolean;
  /** Normalized free-text tokens; every token must prefix-match a thread. */
  terms: string[];
};

// -----------------------------------------------------------------------------
// Text normalization
// -----------------------------------------------------------------------------

export function normalizeThreadSearchText(value: string) {
  return value
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase();
}

export function tokenizeThreadSearchText(value: string) {
  return [...normalizeThreadSearchText(value).matchAll(/[\p{L}\p{N}]+/gu)]
    .map((match) => match[0])
    .filter((token) => [...token].length >= 2);
}

/** Free-text tokens of a query with its `key:value` filters removed. */
export function normalizeThreadSearchQuery(query: string) {
  return parseThreadSearchQuery(query).terms;
}

// -----------------------------------------------------------------------------
// Query parsing
// -----------------------------------------------------------------------------

/** Query words with source offsets, including the optional space between a filter key and value. */
export function scanThreadSearchQuery(query: string) {
  return [...query.matchAll(QUERY_WORD_PATTERN)];
}

/**
 * Splits a raw query into free-text terms and `key:value` filters. Supported keys are
 * `from`/`sender`, `to`, `involves` (exact participant email), `before`, `after`,
 * `has:attachment`, `is:read|unread|starred`,
 * `label`, and `in:<split>`. Values may be double-quoted to include spaces. Words with an
 * unknown key or an unparsable value are searched as free text.
 */
export function parseThreadSearchQuery(query: string): ThreadSearchQuery {
  const filters: ThreadSearchFilters = {
    from: [],
    involves: [],
    is: [],
    labels: [],
    splitIds: [],
    to: [],
  };
  const terms: string[] = [];
  let hasFilters = false;

  for (const [word] of scanThreadSearchQuery(query)) {
    const filterMatch = FILTER_WORD_PATTERN.exec(word);
    const key = filterMatch?.[1]?.toLowerCase();
    const rawValue = filterMatch?.[2]?.trim().replace(/^"(.*?)"?$/su, '$1') ?? '';
    const value = normalizeThreadSearchText(rawValue).trim().replace(/\s+/gu, ' ');
    let applied = false;

    switch (key) {
      case 'involves':
        if (value) {
          filters.involves.push(normalizeEmail(rawValue));
          applied = true;
        }
        break;
      case 'from':
      case 'sender':
      case 'to':
        if (value) {
          (key === 'to' ? filters.to : filters.from).push(value.replace(/^@/u, ''));
          applied = true;
        }
        break;
      case 'before':
      case 'after': {
        const groups = DATE_PATTERNS.map((pattern) => pattern.exec(value)?.groups).find(Boolean);
        const year = Number(groups?.year);
        const month = Number(groups?.month);
        const day = Number(groups?.day);
        const date = new Date(year, month - 1, day);
        if (
          groups &&
          date.getFullYear() === year &&
          date.getMonth() === month - 1 &&
          date.getDate() === day
        ) {
          if (key === 'before') {
            filters.before = Math.max(filters.before ?? Number.NEGATIVE_INFINITY, date.getTime());
          } else {
            filters.after = Math.min(filters.after ?? Number.POSITIVE_INFINITY, date.getTime());
          }
          applied = true;
        }
        break;
      }
      case 'has':
        if (value === 'attachment' || value === 'attachments') {
          filters.hasAttachment = true;
          applied = true;
        }
        break;
      case 'is':
        if ((THREAD_SEARCH_STATES as readonly string[]).includes(value)) {
          filters.is.push(value as ThreadSearchState);
          applied = true;
        }
        break;
      case 'label':
        if (value) {
          filters.labels.push(value);
          applied = true;
        }
        break;
      case 'in': {
        const split = MAIL_SPLITS.find(
          (candidate) => candidate.splitId === value || candidate.label.toLowerCase() === value
        );
        if (split) {
          filters.splitIds.push(split.splitId);
          applied = true;
        }
        break;
      }
      default:
        break;
    }

    if (applied) {
      hasFilters ||= value.length > 0;
    } else {
      terms.push(...tokenizeThreadSearchText(word));
    }
  }

  return { filters, hasFilters, terms };
}
